<template>
  <base-view>
    <div class="page-fill bg-gray-lighter">

      <!-- Hero -->
      <div
        class="relative w-full bg-center bg-cover"
        :style="{ backgroundImage: 'url(' + heroImage + ')' }"
      >
        <div class="absolute inset-0 hero-overlay"></div>
        <section class="relative flex flex-col items-center gap-4 max-w-3xl px-6 pt-16 pb-10 mx-auto text-center">
          <h1 class="m-0 text-4xl font-extrabold leading-tight text-gray-900">What's Covered?</h1>
          <p class="max-w-lg m-0 text-lg text-gray-700">
            Search any product or service to see if it qualifies for your benefit account.
          </p>
          <div class="relative w-full max-w-xl mt-2">
            <input
              v-model="searchValue"
              type="text"
              autocomplete="off"
              aria-label="Search eligible expenses"
              placeholder='Search expenses, e.g. "acupuncture" or "sunscreen"'
              class="box-border w-full py-4 pl-12 pr-11 text-base bg-white border border-gray-dark rounded-lg shadow-sm outline-none focus:border-primary-light focus:ring-2 focus:ring-primary-light"
            />
            <span class="absolute flex items-center justify-center w-5 h-5 text-gray-500 pointer-events-none left-4 search-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
              </svg>
            </span>
            <button
              v-show="searchValue"
              type="button"
              @click="searchValue = ''"
              aria-label="Clear search"
              class="absolute flex items-center justify-center w-5 h-5 text-gray-500 cursor-pointer right-4 search-icon hover:text-gray-700"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </section>
      </div>

      <!-- Controls -->
      <div class="max-w-4xl mx-auto px-6 pt-5 pb-3 flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3">

        <!-- Plan tab toggle -->
        <div class="plan-tabs w-full sm:w-auto" role="group" aria-label="Filter by plan">
          <button
            type="button"
            class="plan-tab"
            :class="{ 'plan-tab-active': selectedPlan === 'ALL' }"
            :aria-pressed="selectedPlan === 'ALL'"
            @click="selectedPlan = 'ALL'"
          >All</button>
          <button
            v-for="p in availablePlans"
            :key="p"
            type="button"
            class="plan-tab"
            :class="{ 'plan-tab-active': selectedPlan === p }"
            :aria-pressed="selectedPlan === p"
            :title="p === 'HRA¹' ? HRA_FOOTNOTE : undefined"
            @click="selectedPlan = p"
          >{{ planTabLabel(p) }}</button>
        </div>

        <!-- Eligibility pills -->
        <div class="flex flex-wrap gap-2 sm:mx-auto" role="group" aria-label="Filter by eligibility">
          <button type="button" class="status-pill status-pill-q" :class="{ 'status-pill-active': showEligible }" :aria-pressed="showEligible" @click="showEligible = !showEligible">
            <span class="status-dot" aria-hidden="true"></span>Eligible
          </button>
          <button type="button" class="status-pill status-pill-p" :class="{ 'status-pill-active': showPotential }" :aria-pressed="showPotential" @click="showPotential = !showPotential">
            <span class="status-dot" aria-hidden="true"></span>Potential
          </button>
          <button type="button" class="status-pill status-pill-n" :class="{ 'status-pill-active': showNot }" :aria-pressed="showNot" @click="showNot = !showNot">
            <span class="status-dot" aria-hidden="true"></span>Not Eligible
          </button>
        </div>
      </div>

      <!-- Alpha nav -->
      <nav
        v-if="!isLoading && !loadError"
        class="alpha-row max-w-4xl mx-auto px-6 mb-4"
        aria-label="Jump to letter"
      >
        <a
          v-for="letter in alphabet"
          :key="letter"
          :href="presentLetters.has(letter) ? '#' + letterAnchor(letter) : null"
          class="alpha-link"
          :class="presentLetters.has(letter) ? 'alpha-link-active' : 'alpha-link-inert'"
          :tabindex="presentLetters.has(letter) ? 0 : -1"
          :aria-disabled="!presentLetters.has(letter)"
          @click.prevent="scrollToLetter(letter)"
          >{{ letter }}</a
        >
      </nav>

      <!-- Main content -->
      <div class="max-w-4xl mx-auto px-6 pb-16">

        <div v-if="isLoading" class="bg-white rounded-xl border border-gray-200 py-16 text-center">
          <p class="text-sm font-semibold text-gray-700">Loading eligibility data…</p>
        </div>

        <div v-else-if="loadError" class="bg-white rounded-xl border border-gray-200 py-16 text-center px-6">
          <p class="text-sm font-semibold text-gray-700 mb-2">We couldn't load the eligibility list right now.</p>
          <p class="text-sm text-gray-600 mb-6">Please try again in a moment.</p>
          <button type="button" @click="fetchData" class="px-5 py-2 text-sm font-bold text-white rounded-full bg-primary hover:bg-primary-mid transition-colors">
            Retry
          </button>
        </div>

        <template v-else>
          <span class="block text-xs font-bold tracking-wide text-gray-700 uppercase mb-3" aria-live="polite">
            {{ resultCount }} result{{ resultCount !== 1 ? 's' : '' }}
          </span>

          <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div class="table-scroll-wrap">
              <table class="expense-table">
                <thead>
                  <tr>
                    <th class="th-expense">Expense</th>
                    <th class="th-plan">{{ selectedPlan === 'ALL' ? 'Plans' : 'Eligibility' }}</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-if="!noResults">
                    <template v-for="group in groupedItems">
                      <tr :key="'letter-' + group.letter">
                        <td :colspan="colSpan" class="letter-divider" :id="letterAnchor(group.letter)">
                          {{ group.letter }}
                        </td>
                      </tr>
                      <tr
                        v-for="item in group.items"
                        :key="item.name"
                        class="expense-row"
                      >
                        <td class="td-expense">
                          <div class="expense-name">{{ item.name }}</div>
                          <div v-if="item.description" class="expense-desc">{{ item.description }}</div>
                        </td>
                        <td class="td-status">
                          <div class="badge-wrap">
                            <template v-for="p in availablePlans">
                              <span
                                v-if="item.planData[p]"
                                :key="p"
                                class="status-badge"
                                :class="['status-badge-' + item.planData[p].code.toLowerCase(), p === 'HRA¹' ? 'has-tooltip' : '']"
                                :data-tooltip="p === 'HRA¹' ? HRA_FOOTNOTE : undefined"
                              >
                                <svg v-if="item.planData[p].code === 'Q'" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>
                                <svg v-else-if="item.planData[p].code === 'N'" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
                                <span v-else aria-hidden="true" class="tilde">~</span>
                                <template v-if="p === 'HRA¹'">HRA<sup class="fn-sup" @click.stop.prevent="scrollToFootnote" aria-label="See footnote">*</sup></template>
                                <template v-else>{{ planTabLabel(p) }}</template>
                              </span>
                            </template>
                          </div>
                        </td>
                      </tr>
                    </template>
                  </template>

                  <tr v-if="noResults">
                    <td :colspan="colSpan" class="no-results-cell">
                      <p class="text-sm font-semibold text-gray-700 mb-1">No matches for "{{ searchValue }}"</p>
                      <p class="text-sm text-gray-500">Try different keywords or clear your filters.</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <p v-if="availablePlans.includes('HRA¹')" id="hra-footnote" class="text-xs text-gray-500 mt-3">* Eligible expenses in an HRA will vary depending on plan design.</p>
        </template>
      </div>

    </div>
  </base-view>
</template>

<script>
import axios from "axios";
import BaseView from "@/components/layout/BaseView.vue";

const SHEET_ID = "1adBidvKf_M0RjsWlt1UES9D_qH20wUaljGqwmh7Yl3s";
const SHEET_KEY = "AIzaSyCJiw7Zod2S564cCR2CdsfFxzdqRvhDjvI";
const SHEET_TABS = ["NEW FSA/HSA/LPF", "NEW DCA", "NEW Commuter"];

const HRA_FOOTNOTE = "Eligible expenses in an HRA will vary depending on plan design.";

const PLAN_QUERY_MAP = {
  fsa:     "Medical FSA",
  hsa:     "HSA",
  dca:     "Dependent Care FSA (DCA)",
  transit: "Commuter Transit (TRN)"
};

// Display labels for plan tab buttons
const PLAN_LABELS = {
  "Medical FSA":              "Medical FSA",
  "Limited-Purpose FSA":      "Limited FSA",
  "HSA":                      "HSA",
  "HRA¹":                     "HRA",
  "Dependent Care FSA (DCA)": "Dep. Care (DCA)",
  "Commuter Parking (PKG)":   "Parking (PKG)",
  "Commuter Transit (TRN)":   "Transit (TRN)"
};

// Preferred display order for plan tabs
const PLAN_ORDER = [
  "Medical FSA",
  "Limited-Purpose FSA",
  "HSA",
  "HRA¹",
  "Dependent Care FSA (DCA)",
  "Commuter Parking (PKG)",
  "Commuter Transit (TRN)"
];

// Normalize inconsistent plan names from the sheet
function normalizePlan(plan) {
  if (plan === "Transit (TRN)") return "Commuter Transit (TRN)";
  return plan;
}

export default {
  name: "WhatsCoveredRedesign",
  components: { BaseView },
  data() {
    return {
      allData: [],
      isLoading: true,
      loadError: false,
      selectedPlan: "ALL",
      searchValue: "",
      showEligible: true,
      showPotential: true,
      showNot: true,
      alphabet: "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split(""),
      heroImage: require("@/assets/bg-whats-covered.jpg"),
      HRA_FOOTNOTE
    };
  },
  computed: {
    availablePlans() {
      const plans = new Set(this.allData.map(d => d.plan));
      return PLAN_ORDER.filter(p => plans.has(p));
    },
    colSpan() {
      return 2;
    },
    filteredItems() {
      const search = this.searchValue.trim().toLowerCase();
      const codeAllowed = { Q: this.showEligible, P: this.showPotential, N: this.showNot };

      // Build map: expense name → { description, planData: { FSA: {code}, DCA: {code} } }
      const map = {};
      this.allData.forEach(row => {
        if (!map[row.name]) {
          map[row.name] = { name: row.name, description: "", planData: {} };
        }
        if (!map[row.name].description && row.description) {
          map[row.name].description = row.description;
        }
        map[row.name].planData[row.plan] = { code: row.code };
      });

      return Object.values(map)
        .filter(item => {
          if (this.selectedPlan === "ALL") {
            return Object.values(item.planData).some(pd => codeAllowed[pd.code]);
          }
          return item.planData[this.selectedPlan] &&
            codeAllowed[item.planData[this.selectedPlan].code];
        })
        .filter(item =>
          !search ||
          item.name.toLowerCase().includes(search) ||
          item.description.toLowerCase().includes(search)
        )
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    groupedItems() {
      const groups = {};
      this.filteredItems.forEach(item => {
        const letter = item.name.charAt(0).toUpperCase();
        if (!groups[letter]) groups[letter] = [];
        groups[letter].push(item);
      });
      return Object.keys(groups).sort().map(letter => ({ letter, items: groups[letter] }));
    },
    presentLetters() {
      return new Set(this.groupedItems.map(g => g.letter));
    },
    resultCount() {
      return this.filteredItems.length;
    },
    noResults() {
      return !this.isLoading && !this.loadError && this.filteredItems.length === 0;
    }
  },
  created() {
    const planQuery = this.$route.query.plan;
    if (planQuery && PLAN_QUERY_MAP[planQuery]) {
      this.selectedPlan = PLAN_QUERY_MAP[planQuery];
    }
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.isLoading = true;
      this.loadError = false;
      try {
        const responses = await Promise.all(
          SHEET_TABS.map(tab =>
            axios.get(
              `https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}/values/${encodeURIComponent(tab)}?key=${SHEET_KEY}`
            )
          )
        );
        const expanded = [];
        responses.forEach(res => {
          const rows = res.data.values || [];
          // Row 0 is the header on every tab — skip it
          rows.slice(1).forEach(row => {
            const name = (row[0] || "").trim();
            const code = (row[1] || "").toUpperCase().trim();
            const description = (row[2] || "").trim();
            const plans = (row[3] || "")
              .split(",")
              .map(p => normalizePlan(p.trim()))
              .filter(Boolean);
            if (!name || !code || !plans.length) return;
            plans.forEach(plan => expanded.push({ name, code, description, plan }));
          });
        });
        this.allData = expanded;
      } catch (e) {
        this.loadError = true;
      } finally {
        this.isLoading = false;
      }
    },
    planLabel(plan) {
      return PLAN_LABELS[plan] || plan;
    },
    planTabLabel(plan) {
      return PLAN_LABELS[plan] || plan;
    },
    statusLabel(code) {
      return { Q: "Eligible", P: "Potentially Eligible", N: "Not Eligible" }[code] || code;
    },
    shortStatusLabel(code) {
      return { Q: "Eligible", P: "Potential", N: "Not Eligible" }[code] || code;
    },
    letterAnchor(letter) {
      return `letter-${letter}`;
    },
    scrollToLetter(letter) {
      if (!this.presentLetters.has(letter)) return;
      const el = document.getElementById(this.letterAnchor(letter));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    scrollToFootnote() {
      const el = document.getElementById("hra-footnote");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }
};
</script>

<style scoped>
.page-fill { min-height: calc(100vh - 10rem); }

/* Hero */
.hero-overlay {
  background: linear-gradient(180deg, rgba(255,255,255,0.62) 0%, rgba(255,255,255,0.88) 45%, #fafafa 75%, #fafafa 100%);
}
.search-icon {
  top: calc(50% - 2px);
  transform: translateY(-50%);
}

/* Plan tab toggle — mirrors support portal audience toggle */
.plan-tabs {
  display: flex;
  padding: 4px;
  border-radius: 999px;
  background-color: #e5e7eb;
  gap: 0;
  overflow-x: auto;
  scrollbar-width: none;
}
.plan-tabs::-webkit-scrollbar { display: none; }
.plan-tab {
  padding: 0.5rem 1.125rem;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: #4b5563;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  outline: none;
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;
  transition: background-color 0.15s, color 0.15s, box-shadow 0.15s;
}
.plan-tab:focus-visible { outline: 2px solid #096843; outline-offset: 2px; }
.plan-tab-active {
  background-color: #ffffff;
  color: #096843;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12);
}

/* Eligibility pills */
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0.4375rem 0.75rem;
  border-radius: 999px;
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  border: 1px solid #e5e7eb;
  background-color: #ffffff;
  color: #6b7280;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  transition: background-color 0.1s, border-color 0.1s, color 0.1s;
}
.status-pill:focus-visible { outline: 2px solid #096843; outline-offset: 2px; }
.status-dot { width: 7px; height: 7px; border-radius: 50%; flex: none; background-color: #d1d5db; }
.status-pill-q.status-pill-active { background-color: #dcfce7; color: #166534; border-color: transparent; }
.status-pill-q.status-pill-active .status-dot { background-color: #16a34a; }
.status-pill-p.status-pill-active { background-color: #fef9c3; color: #854d0e; border-color: transparent; }
.status-pill-p.status-pill-active .status-dot { background-color: #ca8a04; }
.status-pill-n.status-pill-active { background-color: #fee2e2; color: #991b1b; border-color: transparent; }
.status-pill-n.status-pill-active .status-dot { background-color: #dc2626; }

/* Alpha nav */
.alpha-row { display: flex; gap: 2px; overflow-x: auto; scrollbar-width: none; }
.alpha-row::-webkit-scrollbar { display: none; }
.alpha-link {
  width: 26px; height: 26px; flex: none;
  display: flex; align-items: center; justify-content: center;
  border-radius: 6px; font-size: 0.75rem; font-weight: 700;
  text-decoration: none; outline: none; -webkit-tap-highlight-color: transparent;
}
.alpha-link:focus-visible { outline: 2px solid #096843; outline-offset: 2px; }
.alpha-link-active { color: #096843; background-color: #dcfce7; }
.alpha-link-inert { color: #d1d5db; cursor: default; pointer-events: none; }

/* Table */
.table-scroll-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }

.expense-table {
  width: 100%;
  border-collapse: collapse;
}

.th-expense, .th-plan {
  padding: 0.625rem 1.125rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9ca3af;
  text-align: left;
  white-space: nowrap;
}
.th-plan { text-align: right; width: 420px; }

.letter-divider {
  padding: 0.375rem 1.125rem;
  background-color: #f0fdf4;
  border-bottom: 1px solid #e5e7eb;
  border-top: 1px solid #e5e7eb;
  font-size: 0.6875rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: #096843;
  scroll-margin-top: 80px;
}

.expense-row { border-bottom: 1px solid #f3f4f6; transition: background-color 0.1s; }
.expense-row:last-child { border-bottom: none; }
.expense-row:hover { background-color: #f9fafb; }

.td-expense { padding: 0.875rem 1.125rem; vertical-align: top; }
.expense-name { font-size: 0.9375rem; font-weight: 600; color: #111827; line-height: 1.4; }
.expense-desc {
  margin-top: 0.25rem;
  font-size: 0.8125rem;
  color: #6b7280;
  line-height: 1.5;
}

.td-status { padding: 0.875rem 1.125rem; vertical-align: middle; text-align: right; }
.badge-wrap { display: flex; flex-wrap: nowrap; gap: 4px; justify-content: flex-end; }

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
}
.status-badge-q { background-color: #dcfce7; color: #166534; }
.status-badge-p { background-color: #fef9c3; color: #854d0e; }
.status-badge-n { background-color: #fee2e2; color: #991b1b; }
.status-badge-highlight { box-shadow: 0 0 0 2px currentColor; }

.fn-sup {
  cursor: pointer;
  font-size: 0.75em;
  vertical-align: super;
  line-height: 0;
  font-weight: 900;
  text-decoration: underline dotted;
  text-underline-offset: 1px;
}
.fn-sup:hover { opacity: 0.75; }

.has-tooltip { position: relative; overflow: visible; }
.has-tooltip::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: calc(100% + 7px);
  right: 0;
  background: #1f2937;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 500;
  line-height: 1.45;
  padding: 6px 10px;
  border-radius: 6px;
  width: 200px;
  white-space: normal;
  z-index: 50;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s;
}
.has-tooltip:hover::after { opacity: 1; }
.tilde { font-size: 0.9rem; line-height: 1; }
.no-data { color: #d1d5db; font-size: 0.875rem; }

.no-results-cell { padding: 3.5rem 1.5rem; text-align: center; }

@media (min-width: 640px) {
  .expense-table { min-width: 480px; }
}

/* Mobile: card layout */
@media (max-width: 639px) {
  .table-scroll-wrap { overflow-x: visible; }

  .expense-table,
  .expense-table tbody,
  .expense-table tr,
  .expense-table td { display: block; width: 100%; }

  .expense-table thead { display: none; }

  .letter-divider { padding: 0.375rem 1rem; }

  .expense-row {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e5e7eb;
  }
  .expense-row:last-child { border-bottom: none; }

  .td-expense { padding: 0 0 0.5rem; }

  .td-status { padding: 0; text-align: left; }
  .badge-wrap { justify-content: flex-start; flex-wrap: wrap; }
}
</style>
